import { useQuery } from "@tanstack/react-query"
import { ArrowRight, ListChecks, Pause, Receipt } from "lucide-react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { TarjetaNumero } from "@/componentes/ui/TarjetaNumero"
import { usarSesion } from "@/lib/sesion"

/**
 * El panel del sistema: cómo va el negocio.
 *
 * ⚠ **Esto no es «qué me toca hoy».** Eso se mudó a la vista principal del módulo
 * de tareas, que es donde se puede cruzar con el mes y con el tablero. Aquí van los
 * indicadores **operativos y financieros** de la agencia, que son de otro público:
 * quien mira esta pantalla pregunta si algo está atascado o si falta cobrar, no
 * cuáles de las cincuenta tareas abiertas son suyas. Vivieron juntos un rato y
 * quedaba un panel donde había que leer los seis números para saber cuáles hablaban
 * de uno.
 *
 * ⚠ **Esto es el arranque, no el panel terminado.** Lleva los dos indicadores que
 * ya existen medidos —expedientes detenidos y cobranza vencida— y ninguno inventado.
 * Un panel con cifras de relleno se lee igual que uno de verdad, y eso es peor que
 * uno corto: nadie vuelve a preguntar por lo que parece que ya está.
 *
 * ⚠ **Esto no es un informe.** Un informe agrega histórico y contesta «cómo fue el
 * trimestre»; esto cuenta lo que está abierto ahora. Los reportes quedaron fuera de
 * alcance, y meterlos por la puerta de atrás llamándolos «panel» sería el defecto
 * que esa lista previene.
 *
 * ⚠ **Un cero se enseña; un `null` no.** Cero es «no hay nada», que es una noticia
 * buena. `null` es «no te toca» —falta el permiso— y entonces la tarjeta desaparece:
 * enseñar «0 CxC vencidas» a quien no puede ver las CxC le haría creer que la
 * agencia está al día.
 */

interface Panel {
  detenidos: number | null
  cxc_vencidas: number | null
}

export function Inicio() {
  const usuario = usarSesion((e) => e.usuario)

  const consulta = useQuery({
    // La misma clave y el mismo endpoint que el resumen de tareas: es una respuesta
    // pequeña y así las dos pantallas comparten la caché en vez de pedirla dos veces.
    queryKey: ["panel"],
    queryFn: () => api.get<Panel>("/api/v1/panel"),
    refetchInterval: 60_000,
  })

  const nombre = usuario?.nombre?.split(" ")[0] ?? ""
  const p = consulta.data
  const indicadores = p !== undefined && (p.detenidos !== null || p.cxc_vencidas !== null)

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6">
        <h1 className="text-xl font-semibold">{nombre === "" ? "Hola" : `Hola, ${nombre}`}</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Cómo va la agencia.
        </p>
      </header>

      {consulta.isPending && (
        <p role="status" className="py-16 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {consulta.isError && (
        <p
          role="alert"
          className="py-16 text-center text-sm"
          style={{ color: "var(--destructive)" }}
        >
          {consulta.error instanceof ErrorApi
            ? consulta.error.message
            : "No se pudo cargar el panel"}
        </p>
      )}

      {p !== undefined && (
        <>
          {indicadores ? (
            <>
              <h2 className="rotulo mb-3">Operación y cobranza</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {p.detenidos !== null && (
                  <TarjetaNumero
                    valor={p.detenidos}
                    etiqueta="expedientes detenidos"
                    a="/expedientes"
                    icono={<Pause size={16} aria-hidden={true} />}
                    alarma
                    prueba="panel-detenidos"
                  />
                )}
                {p.cxc_vencidas !== null && (
                  <TarjetaNumero
                    valor={p.cxc_vencidas}
                    etiqueta="cuentas por cobrar vencidas"
                    a="/cxc"
                    icono={<Receipt size={16} aria-hidden={true} />}
                    alarma
                    prueba="panel-cxc"
                  />
                )}
              </div>
            </>
          ) : (
            // Sin ninguno de los dos permisos no queda nada que enseñar, y un rótulo
            // encabezando una rejilla vacía se lee como que algo falló.
            <p
              className="superficie py-12 text-center text-sm"
              style={{ color: "var(--fg-muted)" }}
            >
              No tienes indicadores asignados en este panel.
            </p>
          )}

          <h2 className="rotulo mt-8 mb-3">Tu trabajo</h2>
          {/* El camino a lo que se mudó. Sin este enlace, quien tenía la costumbre de
              abrir la pantalla de inicio para ver sus vencimientos se encuentra otra
              cosa y no sabe adónde fueron. */}
          <Link
            to="/tareas"
            data-testid="panel-ir-a-tareas"
            className="tarjeta-interactiva flex items-center justify-between gap-3 p-4 text-sm"
          >
            <span className="flex items-center gap-3">
              <span
                aria-hidden={true}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
                style={{ color: "var(--primary-texto)", backgroundColor: "var(--primary-suave)" }}
              >
                <ListChecks size={16} />
              </span>
              <span>
                <span className="block font-medium">Qué me toca hoy</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  Tus vencimientos, tu mes y el tablero
                </span>
              </span>
            </span>
            <ArrowRight size={16} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
          </Link>
        </>
      )}
    </div>
  )
}
